import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface ServiceSpec {
  label: string;
  value: string;
}

interface ServiceDetailCardProps {
  id: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  features: string[];
  specs: ServiceSpec[];
  machines: string[];
  index: number;
}

export function ServiceDetailCard({
  id,
  icon: Icon,
  title,
  subtitle,
  description,
  image,
  features,
  specs,
  machines,
  index,
}: ServiceDetailCardProps) {
  const isReversed = index % 2 === 1;

  return (
    <motion.div
      id={id}
      className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      {/* Image Container */}
      <div className={`relative overflow-hidden rounded-xl ${isReversed ? 'lg:order-2' : ''}`}>
        <div className="relative">
          <img
            src={image}
            alt={title}
            className="w-full aspect-[4/3] object-cover rounded-xl image-protected"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent rounded-xl" />
        </div>

        {/* Icon Badge — inside the image container to prevent overflow */}
        <div className="absolute top-4 right-4 w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-primary shadow-glow flex items-center justify-center">
          <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground" />
        </div>
      </div>

      {/* Content */}
      <div className={isReversed ? 'lg:order-1' : ''}>
        <span className="text-primary font-mono text-sm uppercase tracking-wider">
          {subtitle}
        </span>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold">{title}</h2>
        <p className="mt-4 text-base lg:text-lg text-muted-foreground leading-relaxed">
          {description}
        </p>

        {/* Features */}
        <div className="mt-6 lg:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {features.map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm">{feature}</span>
            </div>
          ))}
        </div>

        {/* Specs */}
        {specs.length > 0 && (
          <div className="mt-6 lg:mt-8 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {specs.map((spec) => (
              <div
                key={spec.label}
                className="p-3 rounded-lg bg-secondary/50 border border-border/30 technical-data"
              >
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  {spec.label}
                </div>
                <div className="mt-1 font-mono font-semibold text-primary text-sm">
                  {spec.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Machines */}
        {machines.length > 0 && (
          <div className="mt-4 lg:mt-6">
            <span className="text-sm text-muted-foreground">Maschinen: </span>
            <span className="text-sm font-mono">
              {machines.join(' • ')}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
